import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

export interface Preferences {
  motion: 'system' | 'reduce';
  density: 'comfortable' | 'compact';
}
const KEY = 'athenaeum-presentation';
const defaults: Preferences = { motion: 'system', density: 'comfortable' };
function read(): Preferences {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(KEY) ?? 'null');
    if (value && typeof value === 'object') {
      return {
        motion: 'motion' in value && value.motion === 'reduce' ? 'reduce' : 'system',
        density: 'density' in value && value.density === 'compact' ? 'compact' : 'comfortable',
      };
    }
  } catch {
    /* Storage is optional. */
  }
  return defaults;
}
const Context = createContext<{ preferences: Preferences; update: (next: Partial<Preferences>) => void }>({
  preferences: defaults,
  update: () => {},
});
export function PreferencesProvider({ children }: { children: ReactNode }) {
  const [preferences, setPreferences] = useState(read);
  useEffect(() => {
    document.documentElement.dataset.motion = preferences.motion;
    document.documentElement.dataset.density = preferences.density;
    try {
      localStorage.setItem(KEY, JSON.stringify(preferences));
    } catch {
      /* Applies for this visit. */
    }
  }, [preferences]);
  return (
    <Context.Provider
      value={{ preferences, update: (next) => setPreferences((old) => ({ ...old, ...next })) }}
    >
      {children}
    </Context.Provider>
  );
}
export const usePreferences = () => useContext(Context);
